import React, { useState } from "react";
import type {
  FeaturedBandImage,
  FeaturedBandSlot,
  FeaturedBandTemplate,
} from "./featuredBandTypes";
import { FEATURED_BAND_TITLE_MAX, clipFeaturedBandTitle } from "./featuredBandTypes";
import { E, useEdit } from "../InlineEdit";
import "./FeaturedBand.css";

function ShopLink({
  hovered,
  small,
  onClick,
}: {
  hovered: boolean;
  small?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      className={`mte-fb-shop${hovered ? " is-hovered" : ""}${
        small ? " mte-fb-shop--small" : ""
      }`}
      onClick={
        onClick
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      role={onClick ? "link" : undefined}
      aria-label={onClick ? "Shop" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      Shop
      <span className="mte-fb-shop-arrow" aria-hidden>
        →
      </span>
    </div>
  );
}

function ImageOverlay({ label }: { label?: string }) {
  const text = String(label || "").trim();
  if (!text) return null;
  return (
    <span className="mte-fb-img-overlay" aria-hidden={false}>
      <span className="mte-fb-img-overlay-text">{text}</span>
    </span>
  );
}

function ImageFill({
  image,
  className,
  naturalHeight = false,
}: {
  image: FeaturedBandImage;
  className?: string;
  /**
   * Featured Collections: object-fit contain, centered in the fixed frame
   * so the image keeps its aspect ratio on a white background.
   */
  naturalHeight?: boolean;
}) {
  const src = String(image.src || "").trim();
  const label = String(image.label || "").trim();
  if (!src) {
    return (
      <div className={`mte-fb-fill mte-fb-fill--empty ${className || ""}`}>
        <ImageOverlay label={label} />
      </div>
    );
  }
  // Clamp — corrupt/legacy slotImages zoom must not scale past the editor max.
  const zoomRaw = Number(image.zoom);
  const zoom = Number.isFinite(zoomRaw)
    ? Math.min(2.5, Math.max(1, zoomRaw))
    : 1;
  const x = image.x ?? 50;
  const y = image.y ?? 50;
  // naturalHeight (categories/collections) always contain-centers; never cover-crop.
  const fit = naturalHeight || image.fit === "contain" ? "contain" : "cover";
  // Skip scale(1) so auto/catalog tiles aren't "zoomed" via transform + cover.
  const scaleStyle =
    zoom > 1
      ? {
          transform: `scale(${zoom})`,
          transformOrigin: `${x}% ${y}%`,
        }
      : undefined;
  return (
    <div className={`mte-fb-fill ${className || ""}`}>
      <img
        src={src}
        alt={image.alt || label || ""}
        className={`mte-fb-fill-img${fit === "contain" ? " is-contain" : ""}`}
        style={{
          ...scaleStyle,
          objectPosition: `${x}% ${y}%`,
          objectFit: fit,
        }}
        loading="lazy"
        decoding="async"
      />
      <ImageOverlay label={label} />
    </div>
  );
}

/** Featured brands cells carry brandSlug — keep cover so overlays sit on a filled tile. */
function slotUsesBrandOverlayGrid(slot: FeaturedBandSlot): boolean {
  return slot.images.some((img) => String(img.brandSlug || "").trim());
}

function TileFill({
  slot,
  maxImages,
  naturalHeight = false,
  onImageClick,
}: {
  slot: FeaturedBandSlot;
  maxImages: number;
  naturalHeight?: boolean;
  onImageClick?: (image: FeaturedBandImage, index: number) => void;
}) {
  if (slot.mode === "single") {
    return (
      <ImageFill
        image={slot.images[0] || { src: "" }}
        naturalHeight={naturalHeight}
      />
    );
  }
  const brandGrid = slotUsesBrandOverlayGrid(slot);
  const useContain = Boolean(naturalHeight && !brandGrid);
  return (
    <div className="mte-fb-block-grid">
      {slot.images.slice(0, Math.min(4, maxImages)).map((img, i) => {
        const clickable = Boolean(onImageClick && String(img.brandSlug || "").trim());
        return (
          <div
            key={i}
            className={`mte-fb-block-cell${clickable ? " is-interactive" : ""}`}
            onClick={
              clickable
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onImageClick?.(img, i);
                  }
                : undefined
            }
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onImageClick?.(img, i);
                    }
                  }
                : undefined
            }
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={
              clickable
                ? String(img.label || img.alt || "Open image").trim()
                : undefined
            }
          >
            <ImageFill
              image={useContain ? { ...img, fit: "contain" } : { ...img, fit: "cover" }}
              naturalHeight={useContain}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Hero / Split cards — layout matches AddFeaturedCategory mockup:
 * - 1 image: own column (first third on desktop), name beside
 * - 3 images: row of squares; Hero leads with name, Split leads with images
 * - 4 images: 2×2 block at the same square size
 * - Mobile / tablet: always column — images on top, title + Shop below
 */
function CategoryCard({
  slot,
  template,
  isMobile,
  isHero,
  naturalHeight = false,
  onClick,
  onImageClick,
}: {
  slot: FeaturedBandSlot;
  template: FeaturedBandTemplate;
  /** Compact chrome: phone or tablet/split preview. */
  isMobile: boolean;
  isHero: boolean;
  naturalHeight?: boolean;
  onClick?: () => void;
  onImageClick?: (image: FeaturedBandImage, index: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const { get } = useEdit();
  const liveTitle = clipFeaturedBandTitle(
    String(get(`featuredBand.${slot.id}.title`, slot.title) || "").trim(),
  );
  const shown = slot.mode === "single" ? 1 : template.maxImages;
  const asBlock = shown === 4;
  // Split always stacks (image fills its column of the parent). Hero stacks
  // on phone/tablet; desktop Hero stays beside the title and scales with the band.
  const mStacked = isMobile || !isHero;
  // Center title + Shop in the text column for all desktop hero bands
  // (1-image and multi-image), not only the single-image case.
  const centerName = isHero && !isMobile;
  const brandGrid = slotUsesBrandOverlayGrid(slot);
  const useContain = Boolean(naturalHeight && !brandGrid);
  // Desktop size comes from the parent band (container queries). A fixed
  // pixel cap stays tiny inside a wide preview. Inline none beats the
  // stylesheet's historical 196px max.
  const desktopCell: React.CSSProperties = {
    width: "100%",
    maxWidth: "none",
    maxHeight: "none",
    height: "auto",
    aspectRatio: "1",
    flex: "none",
  };

  const renderCell = (img: FeaturedBandImage, i: number, style?: React.CSSProperties) => {
    const clickable = Boolean(onImageClick && String(img.brandSlug || "").trim());
    return (
      <div
        key={i}
        className={`mte-fb-strip-img${clickable ? " is-interactive" : ""}`}
        style={style}
        onClick={
          clickable
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                onImageClick?.(img, i);
              }
            : undefined
        }
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onImageClick?.(img, i);
                }
              }
            : undefined
        }
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={
          clickable
            ? String(img.label || img.alt || "Open image").trim()
            : undefined
        }
      >
        <ImageFill image={img} naturalHeight={useContain} />
      </div>
    );
  };

  const block = (
    <div
      className="mte-fb-strip-img"
      style={
        mStacked
          ? {
              width: "100%",
              maxWidth: "100%",
              maxHeight: "none",
              aspectRatio: "1",
            }
          : desktopCell
      }
    >
      <div className="mte-fb-block-grid">
        {slot.images.slice(0, 4).map((img, i) => {
          const clickable = Boolean(
            onImageClick && String(img.brandSlug || "").trim(),
          );
          return (
            <div
              key={i}
              className={`mte-fb-block-cell${clickable ? " is-interactive" : ""}`}
              onClick={
                clickable
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onImageClick?.(img, i);
                    }
                  : undefined
              }
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        onImageClick?.(img, i);
                      }
                    }
                  : undefined
              }
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={
                clickable
                  ? String(img.label || img.alt || "Open image").trim()
                  : undefined
              }
            >
              <ImageFill
                image={
                  useContain ? { ...img, fit: "contain" } : { ...img, fit: "cover" }
                }
                naturalHeight={useContain}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  const row = slot.images
    .slice(0, shown)
    .map((img, i) =>
      renderCell(
        img,
        i,
        mStacked
          ? {
              flex: shown === 1 ? "0 0 auto" : "1 1 0",
              width: shown === 1 ? "100%" : undefined,
              maxWidth: "none",
              maxHeight: "none",
              height: "auto",
              aspectRatio: "1",
            }
          : desktopCell,
      ),
    );

  const images = asBlock ? block : row;

  const name = (
    <>
      {liveTitle ? (
        <div className={`mte-fb-name${isMobile ? " mte-fb-name--mobile" : ""}`}>
          <E path={`featuredBand.${slot.id}.title`} placeholder="Add title" maxLength={FEATURED_BAND_TITLE_MAX}>
            {slot.title}
          </E>
        </div>
      ) : null}
      <ShopLink hovered={hovered} onClick={onClick} />
    </>
  );

  const cardClass = [
    "mte-fb-card",
    isHero && !isMobile ? "mte-fb-card--hero" : "",
    isMobile ? "mte-fb-card--mobile" : "",
    mStacked ? "mte-fb-card--stacked" : "",
    onClick ? "is-interactive" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const cardStyle: React.CSSProperties = { height: "auto" };

  const bind = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    onClick,
    onKeyDown: onClick
      ? (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }
      : undefined,
    role: onClick ? ("button" as const) : undefined,
    tabIndex: onClick ? 0 : undefined,
  };

  const nameClass = centerName
    ? "mte-fb-card-text mte-fb-card-text--fill"
    : "mte-fb-card-text mte-fb-card-text--left";

  if (mStacked) {
    return (
      <div className={cardClass} style={cardStyle} {...bind}>
        <div className="mte-fb-strip mte-fb-strip--full">{images}</div>
        <div className="mte-fb-card-text">{name}</div>
      </div>
    );
  }

  if (shown === 1) {
    // Split cards share the 4-image strip so toggling 1 ↔ 4 doesn't slide
    // the photo or the name. A lone hero still uses the first-third column.
    const imageCol = isHero ? "mte-fb-hero-third" : "mte-fb-strip";
    return (
      <div className={cardClass} style={cardStyle} {...bind}>
        <div className={imageCol}>{images}</div>
        <div className={nameClass}>{name}</div>
      </div>
    );
  }

  // Hero leads with the name (AddFeatured* mockup). Images shrink to stay
  // inside `.mte-fb-band` so L/R inset matches 1-image.
  if (isHero) {
    return (
      <div className={cardClass} style={cardStyle} {...bind}>
        <div className={nameClass}>{name}</div>
        <div className="mte-fb-strip">{images}</div>
      </div>
    );
  }

  return (
    <div className={cardClass} style={cardStyle} {...bind}>
      <div className="mte-fb-strip">{images}</div>
      <div className={nameClass}>{name}</div>
    </div>
  );
}

/** Trio / Quad tiles — equal columns, square frame, name + Shop beneath. */
function CategoryTile({
  slot,
  isMobile,
  count,
  maxImages,
  naturalHeight = false,
  onClick,
  onImageClick,
}: {
  slot: FeaturedBandSlot;
  isMobile: boolean;
  count: number;
  maxImages: number;
  naturalHeight?: boolean;
  onClick?: () => void;
  onImageClick?: (image: FeaturedBandImage, index: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const { get } = useEdit();
  const liveTitle = clipFeaturedBandTitle(
    String(get(`featuredBand.${slot.id}.title`, slot.title) || "").trim(),
  );
  const perRow = count === 3 ? 3 : 2;
  const gap = 12;
  const tight = isMobile && perRow === 3;
  // Equal columns on every breakpoint — never a fixed 152px that leaves a
  // half-empty row on phone Split-as-tiles (Hero/Split stay cards now).
  const mWidth = `calc(${100 / perRow}% - ${(gap * (perRow - 1)) / perRow}px)`;

  return (
    <div
      className={`mte-fb-tile${onClick ? " is-interactive" : ""}`}
      style={{
        flex: isMobile ? `0 0 ${mWidth}` : "1 1 0",
        maxWidth: isMobile ? mWidth : "none",
        minWidth: 0,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={
        onClick
          ? String(liveTitle || slot.title || "Open featured item").trim()
          : undefined
      }
    >
      <div className="mte-fb-frame">
        <TileFill
          slot={slot}
          maxImages={maxImages}
          naturalHeight={naturalHeight}
          onImageClick={onImageClick}
        />
      </div>
      <div className={`mte-fb-below${tight ? " mte-fb-below--tight" : ""}`}>
        {liveTitle ? (
          <div
            className={`mte-fb-name mte-fb-name--tile${
              tight ? " mte-fb-name--tight" : isMobile ? " mte-fb-name--sm" : ""
            }`}
          >
            <E path={`featuredBand.${slot.id}.title`} placeholder="Add title" maxLength={FEATURED_BAND_TITLE_MAX}>
              {slot.title}
            </E>
          </div>
        ) : null}
        <ShopLink hovered={hovered} small={tight} onClick={onClick} />
      </div>
    </div>
  );
}

export type FeaturedBandDevice = "desktop" | "tablet" | "mobile";

export type FeaturedBandProps = {
  slots: FeaturedBandSlot[];
  template: FeaturedBandTemplate;
  /**
   * Preview device. Mobile/tablet: Hero stacks; Split = 2 columns of
   * stacked cards (like Trio's 3 tile columns). Desktop = side-by-side.
   * Prefer `device` over legacy `isMobile`.
   */
  device?: FeaturedBandDevice;
  /** @deprecated Use `device`. True maps to mobile. */
  isMobile?: boolean;
  /**
   * Featured Collections: images use object-fit contain, centered in the
   * fixed square frame (white background shows around letterboxing).
   * Frame max height stays the same as Categories — layout does not grow.
   */
  naturalHeight?: boolean;
  onSlotClick?: (slot: FeaturedBandSlot, index: number) => void;
  onImageClick?: (
    slot: FeaturedBandSlot,
    image: FeaturedBandImage,
    imageIndex: number,
    slotIndex: number,
  ) => void;
};

function resolveFeaturedBandDevice(
  device?: FeaturedBandDevice,
  isMobile?: boolean,
): FeaturedBandDevice {
  if (device === "tablet" || device === "mobile" || device === "desktop") {
    return device;
  }
  return isMobile ? "mobile" : "desktop";
}

/**
 * Hero/Split (cards) or Trio/Quad (tiles). Split is two stacked columns on
 * every viewport so each image fills its share of the parent section.
 * Desktop Hero stays side-by-side and scales with the band. Trio/Quad stay tiles.
 */
export function FeaturedBand({
  slots,
  template,
  device: deviceProp,
  isMobile: isMobileProp,
  naturalHeight = false,
  onSlotClick,
  onImageClick,
}: FeaturedBandProps) {
  if (!slots.length) return null;
  const device = resolveFeaturedBandDevice(deviceProp, isMobileProp);
  const compact = device === "mobile" || device === "tablet";
  const isCard = template.layout === "card";

  const multi = slots.length > 1;
  // Split (2) is always two columns of stacked cards, on every viewport, so
  // each image fills its share of the parent section. Hero stays one card.
  const stackCards = isCard && multi;
  return (
    <div
      className={`mte-fb-band${compact && !isCard ? " mte-fb-band--wrap" : ""}${
        multi ? " mte-fb-band--multi" : ""
      }${stackCards ? " mte-fb-band--stack mte-fb-band--stack-2" : ""}${
        naturalHeight ? " mte-fb-band--natural" : ""
      }`}
    >
      {slots.map((slot, i) =>
        isCard ? (
          <CategoryCard
            key={slot.id || i}
            slot={slot}
            template={template}
            isMobile={compact}
            isHero={slots.length === 1}
            naturalHeight={naturalHeight}
            onClick={onSlotClick ? () => onSlotClick(slot, i) : undefined}
            onImageClick={
              onImageClick
                ? (image, imageIndex) =>
                    onImageClick(slot, image, imageIndex, i)
                : undefined
            }
          />
        ) : (
          <CategoryTile
            key={slot.id || i}
            slot={slot}
            isMobile={compact}
            count={slots.length}
            maxImages={template.maxImages}
            naturalHeight={naturalHeight}
            onClick={onSlotClick ? () => onSlotClick(slot, i) : undefined}
            onImageClick={
              onImageClick
                ? (image, imageIndex) =>
                    onImageClick(slot, image, imageIndex, i)
                : undefined
            }
          />
        ),
      )}
    </div>
  );
}
